/**
 * StripeConnectOnboarding.tsx
 * 
 * Component for handling Stripe Connect onboarding flow
 * for creators to enable payouts from their monetized Posits.
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ExternalLink, CheckCircle, AlertCircle, RefreshCw, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface StripeAccount {
  stripe_account_id: string;
  onboarding_completed: boolean;
  payouts_enabled: boolean;
  charges_enabled: boolean;
}

interface StripeConnectOnboardingProps {
  onSetupComplete?: () => void;
}

const StripeConnectOnboarding = ({ onSetupComplete }: StripeConnectOnboardingProps) => {
  const [loading, setLoading] = useState(false);
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkStripeAccountStatus();
  }, []);

  // Auto-refresh status when component mounts (helpful after returning from onboarding)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_onboarding') === 'completed') {
      // Wait a moment for Stripe webhook to process, then refresh status
      setTimeout(() => {
        checkStripeAccountStatus();
      }, 2000);
    }
  }, []);

  const checkStripeAccountStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('creator_stripe_accounts')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle instead of single to handle no results gracefully

      if (error) {
        // Handle different types of errors
        if (error.code === 'PGRST116') {
          // No rows returned - this is normal for users without Stripe accounts
        } else if (error.code === '42P01') {
          // Table doesn't exist
        } else if (error.message?.includes('permission denied') || error.message?.includes('406')) {
          // Permission/RLS issues
        } else {
        }
      } else if (data) {
        setStripeAccount(data);
      }
    } catch (error) {
    } finally {
      setCheckingStatus(false);
    }
  };

  const startStripeOnboarding = async () => {
    setLoading(true);
    
    try {
      // The edge function will automatically determine whether to create account or onboarding link
      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: { 
          return_url: `${window.location.origin}/dashboard/settings?stripe_onboarding=completed`,
          refresh_url: `${window.location.origin}/dashboard/settings?stripe_onboarding=refresh`
        }
      });

      if (error) throw error;

      // Redirect to Stripe onboarding
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else if (data.account_link_url) {
        // Backward compatibility
        window.location.href = data.account_link_url;
      } else {
        throw new Error('No onboarding URL received');
      }

    } catch (error: any) {
      toast({
        title: "Setup failed",
        description: error.message || "Failed to start payment setup",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    if (!stripeAccount?.stripe_account_id) return;
    
    setLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
        body: { 
          action: 'create_dashboard_link',
          account_id: stripeAccount.stripe_account_id
        }
      });

      if (error) throw error;

      if (data.dashboard_url) {
        window.open(data.dashboard_url, '_blank');
      } else {
        throw new Error('No dashboard URL received');
      }

    } catch (error: any) {
      toast({
        title: "Dashboard access failed",
        description: error.message || "Failed to open Stripe dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!stripeAccount) {
      return <Badge variant="destructive">Not Set Up</Badge>;
    }

    if (!stripeAccount.onboarding_completed) {
      return <Badge variant="secondary">Setup In Progress</Badge>;
    }

    if (!stripeAccount.charges_enabled || !stripeAccount.payouts_enabled) {
      return <Badge variant="secondary">Verification Pending</Badge>;
    }

    return <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>;
  };

  const canAcceptPayments = stripeAccount?.onboarding_completed && 
                           stripeAccount?.charges_enabled && 
                           stripeAccount?.payouts_enabled;

  if (checkingStatus) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            <div>
              <CardTitle>Payment Setup</CardTitle>
              <CardDescription>
                Set up payments to sell content through your Posit
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCheckingStatus(true);
                checkStripeAccountStatus();
              }}
              disabled={checkingStatus}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`h-4 w-4 ${checkingStatus ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3 p-4">
        {!stripeAccount ? (
          <div className="py-2">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <h3 className="text-base font-semibold mb-1">Payment Account Required</h3>
                <p className="text-muted-foreground mb-2">
                  To sell content, you'll need to set up a payment account with Stripe Connect.
                  This allows you to receive payments directly.
                </p>
                <Button onClick={startStripeOnboarding} disabled={loading}>
                  {loading ? "Starting Setup..." : "Set Up Payments"}
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        ) : canAcceptPayments ? (
          <div className="py-2">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold mb-1">Payment Setup Complete!</h3>
                <p className="text-muted-foreground mb-2">
                  You can now sell content through your Posit. You'll receive{" "}
                  <strong>90%</strong> of each sale (10% platform fee).
                </p>
                <div className="flex gap-3 justify-start">
                  <Button 
                    onClick={openStripeDashboard} 
                    disabled={loading}
                    variant="outline"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    {loading ? "Loading..." : "Manage Payouts"}
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                  {onSetupComplete && (
                    <Button onClick={onSetupComplete} variant="outline">
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-2">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold mb-1">Setup In Progress</h3>
                <p className="text-muted-foreground mb-2">
                  {!stripeAccount.onboarding_completed
                    ? "Please complete your Stripe Connect onboarding to start selling content."
                    : "Your account is being verified. This usually takes a few minutes."
                  }
                </p>
                
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    {stripeAccount.onboarding_completed ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <div className="h-4 w-4 border-2 border-muted-foreground rounded-full" />
                    )}
                    <span>Account Information Complete</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {stripeAccount.charges_enabled ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <div className="h-4 w-4 border-2 border-muted-foreground rounded-full" />
                    )}
                    <span>Payment Processing Enabled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {stripeAccount.payouts_enabled ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <div className="h-4 w-4 border-2 border-muted-foreground rounded-full" />
                    )}
                    <span>Payouts Enabled</span>
                  </div>
                </div>

                {!stripeAccount.onboarding_completed && (
                  <Button 
                    onClick={startStripeOnboarding} 
                    disabled={loading}
                    className="mt-2"
                  >
                    {loading ? "Loading..." : "Continue Setup"}
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {stripeAccount && (
          <div className="text-xs text-muted-foreground border-t pt-3">
            Account ID: {stripeAccount.stripe_account_id.substring(0, 20)}...
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StripeConnectOnboarding; 